/**
 * Strip personal data from a URL before a tracker sees it.
 *
 * Trackers record URLs: PostHog pageviews, Google's page_location, Sentry's
 * request URL and breadcrumbs. Two things in our URLs must never reach them:
 *
 * - Contact fields. Checkout used to carry the guest's name, email, phone,
 *   address and special requests in the query. It no longer does, but old
 *   links, bookmarks and history entries still do.
 * - The full booking reference (`MA-<epoch>-<random>`). Since 2026-10-08 it is
 *   the key to cancel or change a booking, and it sits in the thank-you URL.
 *   It is cut to the short form (`MA-<epoch>`), which opens nothing.
 */
const CONTACT_PARAMS = [
  "firstName",
  "lastName",
  "fullName",
  "email",
  "phone",
  "transportAddress",
  "address",
  "notes",
];

const FULL_BOOKING_REF = /\b([A-Z]{2,4}-\d{6,})-[A-Za-z0-9]{8,}\b/g;

export function scrubUrl(url: string): string;
export function scrubUrl(url: string | null | undefined): string | null | undefined;
export function scrubUrl(url: string | null | undefined): string | null | undefined {
  if (!url) return url;
  const shortened = url.replace(FULL_BOOKING_REF, "$1");
  const queryAt = shortened.indexOf("?");
  if (queryAt === -1) return shortened;
  const hashAt = shortened.indexOf("#", queryAt);
  const base = shortened.slice(0, queryAt);
  const query = shortened.slice(queryAt + 1, hashAt === -1 ? undefined : hashAt);
  const hash = hashAt === -1 ? "" : shortened.slice(hashAt);
  const params = new URLSearchParams(query);
  let changed = false;
  for (const key of CONTACT_PARAMS) {
    if (params.has(key)) {
      params.delete(key);
      changed = true;
    }
  }
  if (!changed) return shortened;
  const rest = params.toString();
  return `${base}${rest ? `?${rest}` : ""}${hash}`;
}

/** URL-valued properties PostHog sets on its own events. */
const POSTHOG_URL_PROPS = [
  "$current_url",
  "$pathname",
  "$referrer",
  "$initial_current_url",
  "$initial_referrer",
  "$prev_pageview_pathname",
  "$session_entry_url",
] as const;

/** Scrub every URL-valued property on a PostHog event, in place. */
export function scrubPosthogProperties<T extends { properties?: Record<string, unknown> } | null>(event: T): T {
  const props = event?.properties;
  if (!props) return event;
  for (const key of POSTHOG_URL_PROPS) {
    const value = props[key];
    if (typeof value === "string") props[key] = scrubUrl(value);
  }
  // Booking events carry the reference (`booking_id`); send the short form.
  for (const [key, value] of Object.entries(props)) {
    if (typeof value === "string") props[key] = value.replace(FULL_BOOKING_REF, "$1");
  }
  const set = props.$set_once as Record<string, unknown> | undefined;
  if (set) {
    for (const key of POSTHOG_URL_PROPS) {
      const value = set[key];
      if (typeof value === "string") set[key] = scrubUrl(value);
    }
  }
  return event;
}

type SentryLikeEvent = {
  request?: { url?: string; query_string?: unknown; cookies?: unknown; headers?: Record<string, string> };
  breadcrumbs?: SentryLikeBreadcrumb[];
  transaction?: string;
  user?: Record<string, unknown>;
};
type SentryLikeBreadcrumb = { message?: string; data?: Record<string, unknown> };

/** Scrub URLs out of a Sentry breadcrumb (navigation, fetch, xhr). */
export function scrubSentryBreadcrumb<T extends SentryLikeBreadcrumb>(crumb: T): T {
  if (crumb.data) {
    for (const key of ["url", "from", "to"]) {
      const value = crumb.data[key];
      if (typeof value === "string") crumb.data[key] = scrubUrl(value);
    }
  }
  if (typeof crumb.message === "string") crumb.message = scrubUrl(crumb.message);
  return crumb;
}

/**
 * Scrub a Sentry event before it is sent: the request URL and query, cookies,
 * the IP the SDK would attach, and every breadcrumb URL. Error reports used to
 * carry the checkout URL with the guest's contact details in it.
 */
export function scrubSentryEvent<T extends SentryLikeEvent>(event: T): T {
  if (event.request) {
    if (typeof event.request.url === "string") event.request.url = scrubUrl(event.request.url);
    delete event.request.query_string;
    delete event.request.cookies;
    if (event.request.headers) {
      delete event.request.headers.cookie;
      delete event.request.headers.Cookie;
      if (typeof event.request.headers.Referer === "string") {
        event.request.headers.Referer = scrubUrl(event.request.headers.Referer);
      }
      if (typeof event.request.headers.referer === "string") {
        event.request.headers.referer = scrubUrl(event.request.headers.referer);
      }
    }
  }
  if (typeof event.transaction === "string") event.transaction = scrubUrl(event.transaction);
  if (event.user) {
    delete event.user.ip_address;
    delete event.user.email;
  }
  event.breadcrumbs?.forEach(scrubSentryBreadcrumb);
  return event;
}
